/**
 * The Pokémon on the field: the Gen V animated sprite from its sheet (tools/sprites), integer
 * scaled, feet on the spot; or, for a creature without a sheet, a type-colored placeholder shape
 * (circle on offense, diamond on defense, a center dot in the second type). No names on the field:
 * tap a Pokémon for its name plate. Under every one a small HP bar, always on (slice 8c), colored
 * by balance's thresholds and drained by the sim's `hp` events. Shapes are generated white once
 * per size and tinted, so they batch.
 */
import Phaser from 'phaser';
import type { TypeName, UnitId } from '@tdmons/sim';
import { hpLevel, hpRatio } from '../ui/hp';
import { color, num, typeColor } from '../ui/tokens';
import { FEET_BELOW_SPOT_YARDS, hpBarSize, type FieldGeometry } from './geometry';
import type { FieldView, UnitView } from './playView';
import { loopMs, type FacingSheet, type SheetJson } from './sheet';
import { facingFor, jsonKey, textureKey } from './spriteLoader';

export interface UnitSprite {
  unit: UnitId;
  side: 'off' | 'def';
  name: string;
  container: Phaser.GameObjects.Container;
  /** The sprite (or shape): what squashes, flashes, pops and animates. */
  body: Phaser.GameObjects.Image;
  tint: number;
  /** The body's resting scale: the whole-number sprite scale, or 1 for a shape. */
  scale: number;
  /** Local y of the body at rest, and of its top, middle and bottom (for labels and rings). */
  baseY: number;
  top: number;
  mid: number;
  /** Under the HP bar. */
  bottom: number;
  /** The HP bar's fill (origin left, scaled in x to the share of max HP left). */
  hpFill: Phaser.GameObjects.Rectangle;
  /** The idle loop, if this is a sprite; `clockMs` is how far into it the sprite is. */
  loop: { sequence: [number, number][]; clockMs: number } | null;
  /** How many children the container has of its own; effects add theirs after these. */
  parts: number;
}

/** How far apart neighbouring Pokémon start their idle loops. */
const LOOP_PHASE_STEP_MS = 397;
/** Placeholder shape outline, as a share of its size. */
const OUTLINE_SHARE = 0.1;
function ensureShape(scene: Phaser.Scene, key: string, size: number, diamond: boolean): void {
  if (scene.textures.exists(key)) return;
  const g = scene.make.graphics({}, false);
  const r = size / 2;
  const outline = Math.max(1, Math.round(size * OUTLINE_SHARE));
  g.fillStyle(color('border-dark'));
  if (diamond)
    g.fillPoints(
      [
        new Phaser.Math.Vector2(r, 0),
        new Phaser.Math.Vector2(size, r),
        new Phaser.Math.Vector2(r, size),
        new Phaser.Math.Vector2(0, r),
      ],
      true,
    );
  else g.fillCircle(r, r, r);
  g.fillStyle(0xffffff);
  const i = r - outline;
  if (diamond)
    g.fillPoints(
      [
        new Phaser.Math.Vector2(r, r - i),
        new Phaser.Math.Vector2(r + i, r),
        new Phaser.Math.Vector2(r, r + i),
        new Phaser.Math.Vector2(r - i, r),
      ],
      true,
    );
  else g.fillCircle(r, r, i);
  g.generateTexture(key, size, size);
  g.destroy();
}

/** Shape size in pixels for a field scale: a bit over a yard, always even. */
export const shapeSize = (pxPerYard: number) => Math.max(10, Math.round(pxPerYard * 0.6) * 2);

/**
 * The sheet for this facing, with its grid frames added to the texture (frame names "0", "1",
 * ...); null if it did not load (no sheet, offline, 404) and the placeholder should draw.
 */
export function ensureSheet(
  scene: Phaser.Scene,
  id: string,
  side: 'off' | 'def',
): FacingSheet | null {
  const facing = facingFor(side);
  const key = textureKey(id, facing);
  const json = scene.cache.json.get(jsonKey(id)) as SheetJson | undefined;
  if (!json || !scene.textures.exists(key)) return null;
  const sheet = json[facing];
  const texture = scene.textures.get(key);
  if (!texture.has('0')) {
    const { frameWidth: w, frameHeight: h, columns } = sheet;
    for (let i = 0; i < sheet.frames; i++) {
      texture.add(String(i), 0, (i % columns) * w, Math.floor(i / columns) * h, w, h);
    }
  }
  return sheet;
}

/** A hit area at least --tap-min across, centered on a w x h body, in its unscaled pixels. */
function tapArea(w: number, h: number, scale: number, dpr: number): Phaser.Geom.Rectangle {
  const min = Math.ceil((num('tap-min') * dpr) / scale);
  const tw = Math.max(w, min);
  const th = Math.max(h, min);
  return new Phaser.Geom.Rectangle((w - tw) / 2, (h - th) / 2, tw, th);
}

export function makeUnit(
  scene: Phaser.Scene,
  u: UnitView,
  geo: FieldGeometry,
  onTap: (unit: UnitId) => void,
  /** Where this one's idle loop starts, so fourteen Pokémon do not breathe in step. */
  phaseMs = 0,
): UnitSprite {
  const sheet = ensureSheet(scene, u.creatureId, u.side);
  const parts: Phaser.GameObjects.GameObject[] = [];
  let made: Omit<UnitSprite, 'unit' | 'side' | 'name' | 'container' | 'parts' | 'hpFill'>;
  if (sheet) {
    const scale = geo.spriteScale;
    const baseY = Math.round(FEET_BELOW_SPOT_YARDS * geo.pxPerYard);
    const h = sheet.frameHeight * scale;
    const body = scene.add
      .image(0, baseY, textureKey(u.creatureId, facingFor(u.side)), '0')
      .setOrigin(0.5, 1)
      .setScale(scale);
    body.setInteractive(
      tapArea(sheet.frameWidth, sheet.frameHeight, scale, geo.dpr),
      Phaser.Geom.Rectangle.Contains,
    );
    parts.push(body);
    made = {
      body,
      tint: 0xffffff,
      scale,
      baseY,
      top: baseY - h,
      mid: baseY - Math.round(h / 2),
      bottom: baseY,
      loop: { sequence: sheet.sequence, clockMs: phaseMs % loopMs(sheet.sequence) },
    };
  } else {
    const size = shapeSize(geo.pxPerYard);
    const diamond = u.side === 'def';
    const key = `${diamond ? 'diamond' : 'dot'}-${size}`;
    ensureShape(scene, key, size, diamond);
    const tint = typeColor(u.types[0] as TypeName);
    const body = scene.add.image(0, 0, key).setTint(tint);
    body.setInteractive(tapArea(size, size, 1, geo.dpr), Phaser.Geom.Rectangle.Contains);
    parts.push(body);
    const second = u.types[1];
    if (second !== undefined) {
      const innerKey = `dot-${size / 2}`;
      ensureShape(scene, innerKey, size / 2, false);
      parts.push(scene.add.image(0, 0, innerKey).setTint(typeColor(second)));
    }
    made = { body, tint, scale: 1, baseY: 0, top: -size / 2, mid: 0, bottom: size / 2, loop: null };
  }
  made.body.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => onTap(u.unit));
  // The HP bar: a dark track one CSS px around a fill, a CSS px under the feet (or the shape).
  const { w: barW, h: barH, edge } = hpBarSize(geo.pxPerYard, geo.dpr, num('hp-bar-field'));
  const barTop = made.bottom + edge;
  const left = -Math.floor(barW / 2);
  const track = scene.add
    .rectangle(left - edge, barTop, barW + 2 * edge, barH + 2 * edge, color('hp-track'))
    .setOrigin(0, 0);
  const hpFill = scene.add
    .rectangle(left, barTop + edge, barW, barH, color('hp-high'))
    .setOrigin(0, 0);
  parts.push(track, hpFill);
  const container = scene.add.container(0, 0, parts);
  return {
    unit: u.unit,
    side: u.side,
    name: u.name,
    container,
    parts: parts.length,
    ...made,
    bottom: barTop + barH + 2 * edge,
    hpFill,
  };
}

/**
 * Set a Pokémon's HP bar to hp of max: drained over --t-hp-drain when `animate` (a play being
 * watched), else straight there (a new snap, a skip, reduced motion). The color is the level it
 * lands on.
 */
export function setHp(
  scene: Phaser.Scene,
  s: UnitSprite,
  hp: number,
  max: number,
  animate: boolean,
): void {
  const fill = s.hpFill;
  scene.tweens.killTweensOf(fill);
  fill.setFillStyle(color(`hp-${hpLevel(hp, max)}`));
  const ratio = hpRatio(hp, max);
  const ms = num('t-hp-drain');
  if (!animate || ms === 0) {
    fill.setScale(ratio, 1);
    return;
  }
  scene.tweens.add({ targets: fill, scaleX: ratio, duration: ms });
}

/** Every Pokémon in a view, each loop started at a different point. */
export function makeUnits(
  scene: Phaser.Scene,
  view: FieldView,
  geo: FieldGeometry,
  onTap: (unit: UnitId) => void,
): Map<UnitId, UnitSprite> {
  return new Map(
    view.units.map((u, i) => [u.unit, makeUnit(scene, u, geo, onTap, i * LOOP_PHASE_STEP_MS)]),
  );
}

export function makeBall(scene: Phaser.Scene, size: number) {
  const w = Math.max(6, Math.round(size * 0.55));
  const h = Math.max(4, Math.round(w * 0.65));
  if (!scene.textures.exists(`ball-${w}`)) {
    const g = scene.make.graphics({}, false);
    g.fillStyle(color('border-dark'));
    g.fillEllipse(w / 2, h / 2, w, h);
    g.fillStyle(0xffffff);
    g.fillEllipse(w / 2, h / 2, w - 2, h - 2);
    g.generateTexture(`ball-${w}`, w, h);
    g.clear();
    g.fillStyle(0x000000, 0.35);
    g.fillEllipse(w / 2, h / 2, w, h);
    g.generateTexture(`ball-shadow-${w}`, w, h);
    g.destroy();
  }
  const shadow = scene.add.image(0, 0, `ball-shadow-${w}`).setDepth(3);
  const ball = scene.add.image(0, 0, `ball-${w}`).setTint(color('field-ball')).setDepth(4);
  return { ball, shadow };
}

/** A white ring and a small spark, shared by the effects. */
export function ensureFxTextures(
  scene: Phaser.Scene,
  size: number,
): { ring: string; spark: string } {
  const ring = `ring-${size}`;
  const spark = `spark-${size}`;
  if (!scene.textures.exists(ring)) {
    const g = scene.make.graphics({}, false);
    const r = size;
    g.lineStyle(2, 0xffffff);
    g.strokeCircle(r, r, r - 2);
    g.generateTexture(ring, r * 2, r * 2);
    g.clear();
    const s = Math.round(size / 2);
    g.fillStyle(0xffffff);
    g.fillPoints(
      [
        new Phaser.Math.Vector2(s, 0),
        new Phaser.Math.Vector2(s + 2, s - 2),
        new Phaser.Math.Vector2(2 * s, s),
        new Phaser.Math.Vector2(s + 2, s + 2),
        new Phaser.Math.Vector2(s, 2 * s),
        new Phaser.Math.Vector2(s - 2, s + 2),
        new Phaser.Math.Vector2(0, s),
        new Phaser.Math.Vector2(s - 2, s - 2),
      ],
      true,
    );
    g.generateTexture(spark, 2 * s, 2 * s);
    g.destroy();
  }
  return { ring, spark };
}
